const INDENT = "  ";

const VOID_ELEMENTS = new Set([
  "area",
  "base",
  "br",
  "col",
  "embed",
  "hr",
  "img",
  "input",
  "link",
  "meta",
  "param",
  "source",
  "track",
  "wbr",
]);

const RAW_TEXT_ELEMENTS = new Set(["script", "style", "pre", "textarea"]);

const tagName = (tag: string): string => {
  const match = tag.match(/^<\/?\s*([a-zA-Z0-9-]+)/);
  return match ? match[1].toLowerCase() : "";
};

/**
 * Pretty-prints an HTML string by putting each tag on its own line and indenting
 * by nesting depth. Best-effort and non-destructive to structure: it only adds
 * whitespace between tags and collapses runs of whitespace inside text nodes.
 * Content of <script>/<style>/<pre>/<textarea> is preserved verbatim.
 * Malformed input is returned trimmed rather than throwing.
 */
export const formatHtml = (input: string): string => {
  const src = input.replace(/\r\n/g, "\n").trim();
  if (!src) return "";

  const lines: string[] = [];
  let depth = 0;
  let i = 0;

  const pad = () => INDENT.repeat(Math.max(0, depth));

  while (i < src.length) {
    if (src[i] !== "<") {
      const nextTag = src.indexOf("<", i);
      const end = nextTag === -1 ? src.length : nextTag;
      const text = src.slice(i, end).replace(/\s+/g, " ").trim();
      if (text) lines.push(pad() + text);
      i = end;
      continue;
    }

    // Comments and declarations (<!-- -->, <!doctype>) are emitted as-is.
    if (src.startsWith("<!--", i)) {
      const close = src.indexOf("-->", i);
      const end = close === -1 ? src.length : close + 3;
      lines.push(pad() + src.slice(i, end).trim());
      i = end;
      continue;
    }
    if (src[i + 1] === "!") {
      const close = src.indexOf(">", i);
      const end = close === -1 ? src.length : close + 1;
      lines.push(pad() + src.slice(i, end).trim());
      i = end;
      continue;
    }

    const close = src.indexOf(">", i);
    if (close === -1) {
      // Unterminated tag — emit the remainder and stop.
      lines.push(pad() + src.slice(i).trim());
      break;
    }

    const tag = src.slice(i, close + 1);
    const name = tagName(tag);
    const isClosing = tag.startsWith("</");
    const isSelfClosing = tag.endsWith("/>") || VOID_ELEMENTS.has(name);

    if (isClosing) {
      depth = Math.max(0, depth - 1);
      lines.push(pad() + tag.trim());
      i = close + 1;
      continue;
    }

    // Raw-text element: capture everything up to its matching close tag verbatim.
    if (RAW_TEXT_ELEMENTS.has(name) && !isSelfClosing) {
      const rest = src.slice(close + 1);
      const closeMatch = rest.match(new RegExp(`</\\s*${name}\\s*>`, "i"));
      if (closeMatch?.index !== undefined) {
        const content = rest.slice(0, closeMatch.index);
        const closeTag = rest.slice(closeMatch.index, closeMatch.index + closeMatch[0].length);
        lines.push(pad() + tag.trim());
        const contentPad = INDENT.repeat(depth + 1);
        content.split("\n").forEach((line) => {
          if (line.trim()) lines.push(contentPad + line.trim());
        });
        lines.push(pad() + closeTag.trim());
        i = close + 1 + closeMatch.index + closeMatch[0].length;
        continue;
      }
    }

    lines.push(pad() + tag.trim());
    if (!isSelfClosing) depth += 1;
    i = close + 1;
  }

  return lines.join("\n");
};
